/**
 * Exam Tips Data
 *
 * Content structure supports multiple language variants:
 *   'en-fr' — English base with French examples
 *   'en-de' — English base with German examples
 *   'fr'    — Full French (future)
 *   'de'    — Full German (future)
 *
 * Text uses **bold** markers for highlighted phrases,
 * parsed at render time by the ExamTips component. Wrap French/German text
 * in [[...]] to keep browser translation from changing the learning content.
 */

export const FREE_TIP_ID = 'ask-reformulate';

export const examTips = [
  // ─── Tip 6 (FREE) ───────────────────────────────────────
  {
    id: 'ask-reformulate',
    icon: '🔄',
    free: true,
    levels: ['A1', 'A2', 'B1'],
    badge: null,
    title: { en: 'Ask to "Reformulate", Not Just "Repeat"' },
    // The sidebar's name for it: the full title wraps onto two lines there.
    navTitle: { en: 'Ask to Reformulate' },
    content: {
      'en-fr': {
        sections: [
          {
            text: "Ask the examiner to **repeat** if you don't hear well. But if you hear perfectly fine but **don't understand the vocabulary**, ask them to **reformulate** — to say it in different, simpler words.",
          },
          {
            heading: 'How to ask',
            text: 'You only need to remember one of these two options:\n\n• **"[[Pourriez-vous reformuler la question, s\'il vous plaît ?]]"**\n(Could you reformulate the question, please?)\n\n• **"[[Je n\'ai pas bien compris. Pouvez-vous l\'expliquer autrement ?]]"**\n(I didn\'t understand well. Can you explain it differently?)',
          },
          {
            text: 'Asking to reformulate is a smart move — it shows the examiner you want to understand and keep the conversation going.',
          },
        ],
        examples: [
          '"[[Pourriez-vous reformuler la question, s\'il vous plaît ?]]"',
          '"[[Je n\'ai pas bien compris. Pouvez-vous l\'expliquer autrement ?]]"',
        ],
        tldr: 'Ask to "repeat" if you did not hear. Ask to "reformulate" if you did not understand the vocabulary. Memorize one phrase to use.',
      },
      'en-de': {
        sections: [
          {
            text: "Ask the examiner to **repeat** if you don't hear well. But if you hear perfectly fine but **don't understand the vocabulary**, ask them to **reformulate** — to say it in different, simpler words.",
          },
          {
            heading: 'How to ask',
            text: 'You only need to remember one of these two options:\n\n• **"[[Könnten Sie die Frage bitte anders formulieren?]]"**\n(Could you formulate the question differently, please?)\n\n• **"[[Ich habe das nicht ganz verstanden. Können Sie das anders erklären?]]"**\n(I didn\'t quite understand that. Can you explain that differently?)',
          },
          {
            text: 'Asking to reformulate is a smart move — it shows the examiner you want to understand and keep the conversation going.',
          },
        ],
        examples: [
          '"[[Könnten Sie die Frage bitte anders formulieren?]]"',
          '"[[Ich habe das nicht ganz verstanden. Können Sie das anders erklären?]]"',
        ],
        tldr: 'Ask to "repeat" if you did not hear. Ask to "reformulate" if you did not understand the vocabulary. Memorize one phrase to use.',
      },
    },
  },

  // ─── Tip 1 ───────────────────────────────────────────────
  {
    id: 'prepare-introduction',
    icon: '🎤',
    free: false,
    levels: ['A1', 'A2', 'B1'],
    badge: null,
    title: { en: 'Prepare Your Introduction' },
    lockedTitle: { en: 'Introduction' },
    content: {
      'en-fr': {
        sections: [
          {
            text: 'Normally, the self-introduction is the first question asked in every FIDE exam. Use it to make a strong first impression.',
          },
          {
            heading: 'What to include',
            text: 'Your name, age, where you come from, where you live now, what you do (work or study), and why you came to Switzerland. Then add **one sentence about your past** and **one about your future plans**. This shows the examiner you can use different tenses — and earns you bonus grammar points right from the start.',
          },
        ],
        examples: [
          '"[[Bonjour, je m\'appelle Maria. J\'ai 30 ans.]]"',
          '"[[J\'habite à Lausanne depuis deux ans.]]"',
          '"[[Je suis venue en Suisse pour travailler.]]"  ([[passé composé]])',
          '"[[Quand j\'étais petite, je voulais être médecin.]]"  ([[imparfait]] — bonus!)',
          '"[[À l\'avenir, j\'aimerais rester en Suisse.]]"  ([[conditionnel]])',
        ],
        tldr: 'The introduction is normally the first question. Prepare and memorize it. Add a past and a future sentence for bonus grammar points.',
      },
      'en-de': {
        sections: [
          {
            text: 'Normally, the self-introduction is the first question asked in every FIDE exam. Use it to make a strong first impression.',
          },
          {
            heading: 'What to include',
            text: 'Your name, age, where you come from, where you live now, what you do (work or study), and why you came to Switzerland. Then add **one sentence about your past** and **one about your future plans**. This shows the examiner you can use different tenses — and earns you bonus grammar points right from the start.',
          },
        ],
        examples: [
          '"[[Hallo, ich heiße Maria. Ich bin 30 Jahre alt.]]"',
          '"[[Ich wohne seit zwei Jahren in Zürich.]]"',
          '"[[Ich bin wegen der Arbeit in die Schweiz gekommen.]]"  ([[Perfekt]])',
          '"[[Als ich klein war, wollte ich Ärztin werden.]]"  ([[Präteritum]] — bonus!)',
          '"[[In Zukunft möchte ich in der Schweiz bleiben.]]"  ([[Modalverb]])',
        ],
        tldr: 'The introduction is normally the first question. Prepare and memorize it. Add a past and a future sentence for bonus grammar points.',
      },
    },
  },

  // ─── Tip 2 ───────────────────────────────────────────────
  {
    id: 'fluidity-over-grammar',
    icon: '🌊',
    free: false,
    levels: ['A1', 'A2', 'B1'],
    badge: null,
    title: { en: 'Fluidity Over Grammar' },
    lockedTitle: { en: 'Speaking Strategy' },
    content: {
      'en-fr': {
        sections: [
          {
            text: 'In the FIDE exam, **keeping the conversation flowing** is more important than perfect grammar. The examiner wants to see that you can **communicate**, not that you speak perfectly.',
          },
          {
            text: 'If you make a small mistake — like saying "[[le table]]" instead of "[[la table]]" — **don\'t stop to correct yourself**. Just keep talking. Stopping breaks the flow and wastes your limited time.',
          },
          {
            heading: 'What matters most',
            text: '• Show a **wide vocabulary** — use different words, not the same ones again and again.\n• Give **detailed answers** — don\'t just say "[[oui]]" or "[[non]]". Explain why.\n• **Keep talking** — saying more (even with small mistakes) is better than less fluent but perfect sentences.',
          },
        ],
        examples: [
          'You say "[[le table]]" instead of "[[la table]]" — it\'s fine! Keep going.',
          'You forget a word? Describe it: "the place where you buy bread" instead of "[[boulangerie]]".',
        ],
        tldr: "Don't stop for small grammar mistakes. Keep talking. A wide vocabulary and fluent conversation scores higher than perfect grammar with short answers.",
      },
      'en-de': {
        sections: [
          {
            text: 'In the FIDE exam, **keeping the conversation flowing** is more important than perfect grammar. The examiner wants to see that you can **communicate**, not that you speak perfectly.',
          },
          {
            text: 'If you make a small mistake — like saying "[[das Lampe]]" instead of "[[die Lampe]]" — **don\'t stop to correct yourself**. Just keep talking. Stopping breaks the flow and wastes your limited time.',
          },
          {
            heading: 'What matters most',
            text: '• Show a **wide vocabulary** — use different words, not the same ones again and again.\n• Give **detailed answers** — don\'t just say "[[ja]]" or "[[nein]]". Explain why.\n• **Keep talking** — saying more (even with small mistakes) is better than less fluent but perfect sentences.',
          },
        ],
        examples: [
          'You say "[[das Lampe]]" instead of "[[die Lampe]]" — it\'s fine! Keep going.',
          'You forget a word? Describe it: "the place where you buy bread" instead of "[[Bäckerei]]".',
        ],
        tldr: "Don't stop for small grammar mistakes. Keep talking. A wide vocabulary and fluent conversation scores higher than perfect grammar with short answers.",
      },
    },
  },

  // ─── Tip 3 ───────────────────────────────────────────────
  {
    id: 'filler-words',
    icon: '💬',
    free: false,
    levels: ['A1', 'A2', 'B1'],
    badge: null,
    title: { en: 'Use Filler Words to Buy Time' },
    lockedTitle: { en: 'Conversation Technique' },
    content: {
      'en-fr': {
        sections: [
          {
            text: "When you need a moment to think, **don't stay silent**. Silence can feel awkward and wastes exam time. Instead, use natural filler words and phrases. They sound normal in conversation and give you **precious seconds to organize your thoughts**.",
          },
          {
            heading: 'Useful filler phrases',
            text: '• **"[[Alors...]]"** (So...)\n• **"[[En fait...]]"** (Actually...)\n• **"[[C\'est une bonne question...]]"** (That\'s a good question...)\n• **"[[Comment dire...]]"** (How to say...)\n• **"[[Je pense que...]]"** (I think that...)',
          },
          {
            text: "Pick **2 or 3** of these and practice using them naturally. Don't use all of them at once — it should feel like a real conversation!",
          },
        ],
        examples: [
          '"[[Alors... je pense que c\'est important parce que...]]"',
          '"[[C\'est une bonne question... en fait, pour moi...]]"',
        ],
        tldr: 'Use filler phrases like "[[Alors...]]" or "[[En fait...]]" instead of staying silent. They sound natural and give you time to think.',
      },
      'en-de': {
        sections: [
          {
            text: "When you need a moment to think, **don't stay silent**. Silence can feel awkward and wastes exam time. Instead, use natural filler words and phrases. They sound normal in conversation and give you **precious seconds to organize your thoughts**.",
          },
          {
            heading: 'Useful filler phrases',
            text: '• **"[[Also...]]"** (So...)\n• **"[[Na ja...]]"** (Well...)\n• **"[[Das ist eine gute Frage...]]"** (That\'s a good question...)\n• **"[[Wie soll ich sagen...]]"** (How should I say...)\n• **"[[Ich denke...]]"** (I think...)',
          },
          {
            text: "Pick **2 or 3** of these and practice using them naturally. Don't use all of them at once — it should feel like a real conversation!",
          },
        ],
        examples: [
          '"[[Also... ich denke, das ist wichtig, weil...]]"',
          '"[[Das ist eine gute Frage... na ja, für mich...]]"',
        ],
        tldr: 'Use filler phrases like "[[Also...]]" or "[[Na ja...]]" instead of staying silent. They sound natural and give you time to think.',
      },
    },
  },

  // ─── Tip 4 ───────────────────────────────────────────────
  {
    id: 'oral-exam-structure',
    icon: '🧩',
    free: false,
    levels: ['A1', 'A2', 'B1'],
    badge: null,
    title: { en: 'Know the Oral Exam Structure' },
    lockedTitle: { en: 'Oral Exam Structure' },
    content: {
      'en-fr': {
        sections: [
          {
            text: 'The FIDE oral exam follows a structure that changes depending on your level. Knowing how the tasks work gives you a big advantage.',
          },
          {
            heading: '🟢 A1 — Everyday Basics',
            text: '**• Task 1 (Image Description)**: Describe a situation (e.g., at the supermarket), usually with 4 scenes. Use only the present tense to say who is there and what is happening.\n**• Task 2 (Guided Dialogue)**: Do a simple roleplay guided by the 4 scenes. The examiner guides the conversation. Respond with simple sentences.\n**• Task 3 (Basic Questions)**: Answer basic questions about your daily life, to check your everyday vocabulary.',
          },
          {
            heading: '🟡 A2 — Practical Situations & Opinions',
            text: '**• Task 1 (Image Description)**: Describe one detailed picture. Describe what is visible in the present tense.\n**• Task 2 (Roleplay)**: Simulate a practical everyday situation, like being on the phone (e.g., booking an appointment). You might have to negotiate a bit, like suggesting a different time. Use present and polite request forms.\n**• Task 3 (Short Discussion)**: The examiner asks **comparison questions** (advantages and disadvantages) and **past experience questions**. You are expected to use the **[[Passé Composé]]** and **[[Imparfait]]** correctly when talking about the past.',
          },
          {
            heading: '🟠 B1 — Advanced Grammar & Topics',
            text: 'First, you must choose a topic. Then the examiner usually asks 3-4 questions, each testing a **different grammar skill**:\n\n• **General/Personal** (Warmup questions)\n• **Comparison** ([[Comparatif]])\n• **Past Experience** ([[Passé Composé]] + [[Imparfait]])\n• **Future / Hypothetical** ([[Futur Simple]] + [[Conditionnel]]) : remember to **"use [[Futur Simple]] instead of [[Futur Proche]]"** as necessary (common mistake).\n\n',
          },
          {
            text: '⚠️ **Warning:** These are not hard rules. The examiner can decide to change the order, or even skip/add questions due to time constraints.',
          },
        ],
        examples: [],
        tldr: 'A1: Describe scenes, guided dialogue, basic questions. A2: Detailed image, roleplay, comparison or past questions. B1: 3-4 questions testing specific tenses (comparison, past, future).',
      },
      'en-de': {
        sections: [
          {
            text: 'The FIDE oral exam follows a predictable structure that changes depending on your level. Knowing how the tasks work gives you a big advantage.',
          },
          {
            heading: '🟢 A1 — Everyday Basics',
            text: '**• Task 1 (Image Description)**: Describe a situation (e.g., at the supermarket), usually with 4 scenes. Use only the present tense to say who is there and what is happening.\n**• Task 2 (Guided Dialogue)**: Do a simple roleplay guided by the 4 scenes. The examiner guides the conversation. Respond with simple sentences.\n**• Task 3 (Basic Questions)**: Answer basic questions about your daily life, to check your everyday vocabulary.',
          },
          {
            heading: '🟡 A2 — Practical Situations & Opinions',
            text: '**• Task 1 (Image Description)**: Describe one detailed picture. Describe what is visible in the present tense.\n**• Task 2 (Roleplay)**: Simulate a practical everyday situation, like being on the phone (e.g., booking an appointment). You might have to negotiate a bit, like suggesting a different time. Use present and polite request forms.\n**• Task 3 (Short Discussion)**: The examiner asks **comparison questions** (advantages and disadvantages) and **past experience questions**. You are expected to use the **[[Perfekt]]** correctly when talking about the past.',
          },
          {
            heading: '🟠 B1 — Advanced Grammar & Topics',
            text: 'First, you must choose a topic. Then the examiner usually asks 3-4 questions, each testing a **different grammar skill**:\n\n• **General/Personal** (Warmup questions)\n• **Comparison** ([[Komparativ]])\n• **Past Experience** ([[Perfekt]])\n• **Future / Hypothetical** ([[Präsens]] for future / [[Konjunktiv II]]) : normally adviced to use [[Präsens]] + time word instead of [[Futur I]].\n\n',
          },
          {
            text: '⚠️ **Warning:** These are not hard rules. The examiner can decide to change the order, or even skip/add questions due to time constraints.',
          },
        ],
        examples: [],
        tldr: 'A1: Describe scenes, guided dialogue, basic questions. A2: Detailed image, roleplay, comparison or past questions. B1: 3-4 questions testing specific tenses (comparison, past, future).',
      },
    },
  },

  // ─── Tip 5 ───────────────────────────────────────────────
  // {
  //   id: 'grammar-tenses',
  //   icon: '📐',
  //   free: false,
  //   levels: ['A2', 'B1'],
  //   badge: null,
  //   title: { en: 'Grammar Tenses by Level' },
  //   lockedTitle: { en: 'Grammar Essentials' },
  //   content: {
  //     'en-fr': {
  //       sections: [
  //         {
  //           text: 'Each exam level requires specific grammar tenses. Knowing **exactly which tenses to use** is the key to scoring well.',
  //         },
  //         {
  //           heading: 'A2 — You need these tenses',
  //           text: '• **Présent** (present): "Je travaille à Lausanne."\n• **Passé Composé** (past actions): "J\'ai visité la Suisse."\n• **Conditionnel de politesse** (polite requests): "Je voudrais un café, s\'il vous plaît."',
  //         },
  //         {
  //           heading: 'B1 — You also need',
  //           text: '• **Imparfait** (past descriptions): "Quand j\'étais petit, c\'était différent."\n• **Futur Simple** (future plans): "L\'année prochaine, je travaillerai à Genève."\n• **Conditionnel** (wishes): "J\'aimerais apprendre une nouvelle langue."',
  //         },
  //         {
  //           text: 'If you target B1, **always try to use Futur Simple** ("je travaillerai", "j\'irai", "je ferai"). This is what separates A2 from B1!',
  //         },
  //       ],
  //       examples: [
  //         'A2: "Je travaille ici. J\'ai commencé il y a deux ans. Je voudrais changer de travail."',
  //         "B1: \"J'ai visité Paris. C'était magnifique. L'année prochaine, j'irai en Italie. J'aimerais aussi visiter l'Espagne.\"",
  //       ],
  //       tldr: 'A2 needs Présent + Passé Composé + polite Conditionnel. B1 adds Imparfait + Futur Simple + Conditionnel. The Futur Simple is what separates A2 from B1.',
  //     },
  //     'en-de': {
  //       sections: [
  //         {
  //           text: 'Each exam level requires specific grammar tenses. Knowing **exactly which tenses to use** is the key to scoring well.',
  //         },
  //         {
  //           heading: 'A2 — You need these tenses',
  //           text: '• **Präsens** (present): "Ich arbeite in Zürich."\n• **Perfekt** (past actions): "Ich habe die Schweiz besucht."\n• **Polite forms**: "Ich möchte einen Kaffee, bitte."',
  //         },
  //         {
  //           heading: 'B1 — You also need',
  //           text: '• **Konjunktiv II** (wishes/hypothetical): "Ich würde gern mehr reisen."\n• **Modal verbs** (wants/plans): "Ich möchte nächstes Jahr umziehen."',
  //         },
  //         {
  //           heading: 'Important for German B1',
  //           text: '**Do NOT use Futur I** ("Ich werde arbeiten"). It sounds unnatural in spoken German. Instead, use **Präsens + time word**: "Nächstes Jahr arbeite ich in Bern." This is the natural way to talk about the future in German!',
  //         },
  //       ],
  //       examples: [
  //         'A2: "Ich arbeite hier. Ich habe vor zwei Jahren angefangen. Ich möchte die Stelle wechseln."',
  //         'B1: "Letztes Jahr habe ich einen Kurs gemacht. Es war toll. Nächstes Jahr möchte ich nach Bern umziehen. Ich würde gern etwas Neues lernen."',
  //       ],
  //       tldr: 'A2 needs Präsens + Perfekt + polite forms. B1 adds Konjunktiv II + modal verbs. Do NOT use Futur I in German — use Präsens + time word instead.',
  //     },
  //   },
  // },
  // ─── Tip 7 ───────────────────────────────────────────────
  {
    id: 'key-phrases',
    icon: '🗝️',
    free: false,
    levels: ['A1', 'A2', 'B1'],
    badge: null,
    title: { en: 'Memorize Key Phrases' },
    lockedTitle: { en: 'Useful Phrases' },
    content: {
      'en-fr': {
        sections: [
          {
            text: "Having **ready-made phrases** makes speaking much easier. You don't need to build every sentence from zero — just learn these building blocks and **fill in the blanks**.",
          },
          {
            heading: 'Describing an image',
            text: '• **"[[Sur cette image, je vois...]]"** (In this image, I see...)\n• **"[[Il y a une bulle en haut à droite.]]"** (There is a speech bubble at the top right.)',
          },
          {
            heading: 'Talking about the past',
            text: '• **"[[Quand j\'étais petit(e)...]]"** (When I was little...)\n• **"[[Avant, je...]]"** (Before, I...)\n• **"[[L\'année dernière, j\'ai...]]"** (Last year, I...)',
          },
          {
            heading: 'Talking about the future',
            text: '• **"[[Ce serait super si...]]"** (It would be great if...)\n• **"[[J\'aimerais beaucoup...]]"** (I would really like to...)\n• **"[[L\'année prochaine, je...]]"** (Next year, I...)',
          },
          {
            heading: 'Giving your opinion',
            text: '• **"[[À mon avis...]]"** (In my opinion...)\n• **"[[Je pense que...]]"** (I think that...)\n• **"[[Pour moi...]]"** (For me...)',
          },
          {
            heading: 'Connecting ideas',
            text: '• **"[[D\'un côté... de l\'autre...]]"** (On one hand... on the other hand...)\n• **"[[D\'abord... ensuite... finalement...]]"** (First... then... finally...)\n• **"[[En revanche...]]"** (On the other hand...)',
          },
        ],
        examples: [],
        tldr: 'Memorize ready-made phrases for image description, past, future, opinion, and connecting ideas. Fill in the blanks instead of building from zero.',
      },
      'en-de': {
        sections: [
          {
            text: "Having **ready-made phrases** makes speaking much easier. You don't need to build every sentence from zero — just learn these building blocks and **fill in the blanks**.",
          },
          {
            heading: 'Describing an image',
            text: '• **"[[Auf diesem Bild sehe ich...]]"** (In this image, I see...)\n• **"[[Es gibt eine Sprechblase oben rechts.]]"** (There is a speech bubble at the top right.)',
          },
          {
            heading: 'Talking about the past',
            text: '• **"[[Als ich klein war...]]"** (When I was little...)\n• **"[[Früher habe ich...]]"** (In the past I...)\n• **"[[Letztes Jahr habe ich...]]"** (Last year I...)',
          },
          {
            heading: 'Talking about the future',
            text: '• **"[[Das wäre toll, wenn...]]"** (It would be great if...)\n• **"[[Ich würde gern...]]"** (I would like to...)\n• **"[[Nächstes Jahr möchte ich...]]"** (Next year I want to...)',
          },
          {
            heading: 'Giving your opinion',
            text: '• **"[[Meiner Meinung nach...]]"** (In my opinion...)\n• **"[[Ich denke, dass...]]"** (I think that...)\n• **"[[Für mich...]]"** (For me...)',
          },
          {
            heading: 'Connecting ideas',
            text: '• **"[[Einerseits... andererseits...]]"** (On one hand... on the other hand...)\n• **"[[Zuerst... dann... zum Schluss...]]"** (First... then... in the end...)\n• **"[[Dagegen...]]"** (By contrast / On the other hand...)',
          },
        ],
        examples: [],
        tldr: 'Memorize ready-made phrases for image description, past, future, opinion, and connecting ideas. Fill in the blanks instead of building from zero.',
      },
    },
  },

  // ─── Tip 8 ───────────────────────────────────────────────
  {
    id: 'writing-tips',
    icon: '✍️',
    free: false,
    levels: ['A1', 'A2', 'B1'],
    badge: null,
    title: { en: 'Writing Section Tips' },
    lockedTitle: { en: 'Writing Section' },
    content: {
      'en-fr': {
        sections: [
          {
            heading: 'A1 Writing — Forms & Short Messages',
            text: 'At A1, you usually read a short text and **extract information** to fill out a form or complete a very short message/SMS. You don\'t need to write long texts. Basic language is enough, for example writing **"[[à Lausanne]]"** (in Lausanne), a date, or a short answer with key information.',
          },
          {
            heading: 'A2 Writing — Guided Emails & Messages',
            text: 'For A2, you often write a short email or message following clear points. The best trick is to memorize a few key phrases you can reuse.',
          },
          {
            heading: 'Greetings (Start of letter)',
            text: '• **"[[Madame, Monsieur,]]"** (Formal: if you do not know the person)\n• **"[[Madame,]]"** / **"[[Monsieur,]]"** (Formal: if you know who you are writing to)\n• **"[[Cher Paul,]]"** / **"[[Chère Marie,]]"** (Informal: Dear Paul / Marie)',
          },
          {
            heading: 'Useful Middle Phrases',
            text: '• **"[[Je vous écris parce que...]]"** (Formal: I am writing to you because...)\n• **"[[J\'espère que tu vas bien.]]"** (Informal: I hope you are doing well.)\n• **"[[Merci pour votre e-mail.]]"** / **"[[Merci pour ton e-mail.]]"** (Formal / Informal: Thank you for your email.)',
          },
          {
            heading: 'Endings (Closing the letter)',
            text: '• **"[[Je vous envoie les documents en pièce jointe.]]"** (I am sending you the attached documents.)\n• **"[[Merci d\'avance pour votre réponse.]]"** (Thank you in advance for your reply.)\n• **"[[Cordialement,]]"** (Formal: Best regards.)\n• **"[[À bientôt.]]"** (Informal: See you soon.)',
          },
          {
            heading: 'B1 Writing',
            text: 'At B1, the task is still guided, but it can be a short email, a simple formal letter, or an informal email. You need clearer structure, opinions, connectors, and a bit more detail than at A2.',
          },
        ],
        examples: [],
        tldr: 'A1 = fill in forms or complete a very short message. A2 = write a short guided email/message. B1 = write a longer guided text such as an email or simple letter, with opinions and connectors.',
      },
      'en-de': {
        sections: [
          {
            heading: 'A1 Writing — Forms & Short Messages',
            text: 'At A1, you usually read a short text and **extract information** to fill out a form or complete a very short message/SMS. You don\'t need to write long texts. Basic language is enough, for example writing **"[[in Zürich]]"** (in Zurich), a date, or a short answer with key information.',
          },
          {
            heading: 'A2 Writing — Guided Emails & Messages',
            text: 'For A2, you often write a short email or message following clear points. The best trick is to memorize a few key phrases you can reuse.',
          },
          {
            heading: 'Greetings (Start of letter)',
            text: '• **"[[Sehr geehrte Damen und Herren,]]"** (Formal: Dear sir/madam)\n• **"[[Sehr geehrter Herr [Name],]]"** / **"[[Sehr geehrte Frau [Name],]]"** (Formal: Dear Mr./Ms.)\n• **"[[Lieber Paul,]]"** / **"[[Liebe Marie,]]"** (Informal: Dear Paul / Marie)',
          },
          {
            heading: 'Useful Middle Phrases',
            text: '• **"[[Ich schreibe Ihnen, weil...]]"** (Formal: I am writing to you because...)\n• **"[[Ich hoffe, es geht dir gut.]]"** (Informal: I hope you are doing well.)\n• **"[[Vielen Dank für Ihre E-Mail.]]"** / **"[[Danke für deine E-Mail.]]"** (Formal / Informal: Thank you for your email.)',
          },
          {
            heading: 'Endings (Closing the letter)',
            text: '• **"[[Im Anhang finden Sie die Dokumente.]]"** (Attached you will find the documents.)\n• **"[[Ich freue mich auf Ihre Antwort.]]"** (Formal: Looking forward to your reply.)\n• **"[[Mit freundlichen Grüßen]]"** (Formal: Best regards.)\n• **"[[Liebe Grüße]]"** / **"[[Bis bald!]]"** (Informal: Best regards / See you soon!)',
          },
          {
            heading: 'B1 Writing',
            text: 'At B1, the task is still guided, but it can be a short email, a simple formal letter, or an informal email. You need clearer structure, opinions, connectors, and a bit more detail than at A2.',
          },
        ],
        examples: [],
        tldr: 'A1 = fill in forms or complete a very short message. A2 = write a short guided email/message. B1 = write a longer guided text such as an email or simple letter, with opinions and connectors.',
      },
    },
  },
];
